import React from 'react';
import ReactDOM from 'react-dom';

import type { ApiRequestUrlOptions } from '../types';
import { buildUrl } from '../utils/url';
import type { YMapsModulesName } from '../../../../typings/ymaps';

/**
 * Загрузка стороннего скрипта на страницу
 * @param url - откуда грузим
 * @returns Промис завршения загрузки
 */
const fetchScript = (url: string) =>
  new Promise((resolve, reject) => {
    const script = document.createElement('script');

    script.type = 'text/javascript';
    script.onload = resolve;
    script.onerror = reject;
    script.src = url;
    script.async = true;

    document.head.appendChild(script);
  });

/**
 * Загрузка core JS API Яндекс Карт
 * @param options Параметры для построения url запроса
 */
const loadCoreModule = async (options: ApiRequestUrlOptions) => {
  const url = buildUrl(options);
  await fetchScript(url);
  // В доках указано, что необходимо дождаться разрешения промиса ready перед использованием карт
  await window.ymaps3?.ready;
};

/**
 * Загрузка модуля reactify.
 * Использовать только после разрешения промиса loadCoreModule
 */
const loadReactifyModule = async () => {
  const ymaps3React = await ymaps3.import('@yandex/ymaps3-reactify');
  const reactify = ymaps3React.reactify.bindTo(React, ReactDOM as any);
  window.ymaps3Modules = {};
  window.ymaps3Modules.reactify = reactify;
};

/**
 * Загрузка модуля controls.
 * Использовать только после разрешения промиса loadCoreModule и loadReactifyModule
 * @see https://yandex.ru/dev/jsapi30/doc/ru/ref/packages/controls/
 */
const loadControlsModule = async () => {
  if (!window?.ymaps3Modules?.reactify)
    throw new Error('Cant find reactify module');
  const controlsModule = await ymaps3.import('@yandex/ymaps3-controls@0.0.1');
  window.ymaps3Modules.controls =
    window.ymaps3Modules.reactify.module(controlsModule);
};

/**
 * Загрузка модуля markers
 * Использовать только после разрешения промиса loadCoreModule и loadReactifyModule
 * @see https://yandex.ru/dev/jsapi30/doc/ru/ref/packages/markers/
 */
const loadMarkersModule = async () => {
  if (!window?.ymaps3Modules?.reactify)
    throw new Error('Cant find reactify module');
  const markersModule = await ymaps3.import('@yandex/ymaps3-markers@0.0.1');
  window.ymaps3Modules.markers =
    window.ymaps3Modules.reactify.module(markersModule);
};

/**
 * Загрузка модуля clusterer
 * Использовать только после разрешения промиса loadCoreModule и loadReactifyModule
 * @see https://yandex.ru/dev/jsapi30/doc/ru/ref/packages/clusterer/
 */
const loadClustererModule = async () => {
  if (!window?.ymaps3Modules?.reactify)
    throw new Error('Cant find reactify module');
  const clustererModule = await ymaps3.import('@yandex/ymaps3-clusterer@0.0.1');
  window.ymaps3Modules.clusterer =
    window.ymaps3Modules.reactify.module(clustererModule);
};

/**
 * Загрузка JS API Яндекс карт и дополнительных модулей Яндекс карт.
 * При использовании компонентов из Яндекс.Карт нужно ориентироваться на
 * возвращаемый этой функцией промис.
 * JS API (ymaps3) сохраняется в window, как и дополнительные модули (ymaps3Modules)
 *
 * Дока по картам:
 * @see https://yandex.ru/dev/jsapi30/doc/ru/
 *
 * @param options Параметры для получения JS API Яндекс Карт
 * @param modules Названия подгружаемых модулей
 * @returns Промис, для асинхронной загрузки используемых компонентов, например через React.lazy
 */
const loadMapModules = async (
  options: ApiRequestUrlOptions,
  modules: YMapsModulesName[],
) => {
  await loadCoreModule(options);
  await loadReactifyModule();

  if (modules.includes('controls')) {
    await loadControlsModule();
  }
  if (modules.includes('markers')) {
    await loadMarkersModule();
  }
  if (modules.includes('clusterer')) {
    await loadClustererModule();
  }
};

export default loadMapModules;
